// Regras puras do formulário de pedido de compra — testáveis sem renderizar.
//
// Nasceu do caso de 10/09/2026: "Adicionar parcela" cria a linha em branco e o
// formulário mandava o POST assim; o backend recusava com 400 e a tela mostrava
// "Failed to create purchase order", sem dizer qual campo.

export interface InstallmentLike {
  value: number;
  due_date?: string;
}

/** Chave, no mapa de erros do formulário, do campo de uma parcela (índice 0-based). */
export const installmentErrorKey = (idx: number, field: 'due_date' | 'value'): string =>
  `installments.${idx}.${field}`;

/**
 * Erros das parcelas antes de enviar: cada linha precisa de vencimento e de valor
 * maior que zero — as mesmas regras do backend (`CreatePurchaseOrderInstallmentRequest`),
 * conferidas aqui para a pessoa ver o erro na própria linha, sem ida à API.
 */
export function validateInstallments(installments: InstallmentLike[]): Record<string, string> {
  const errors: Record<string, string> = {};
  installments.forEach((inst, idx) => {
    const n = idx + 1;
    if (!inst.due_date || !inst.due_date.trim()) {
      errors[installmentErrorKey(idx, 'due_date')] = `Parcela ${n}: informe o vencimento`;
    }
    if (!(inst.value > 0)) {
      errors[installmentErrorKey(idx, 'value')] = `Parcela ${n}: informe um valor maior que zero`;
    }
  });
  return errors;
}

/**
 * Diferença entre a soma das parcelas e o total do pedido (parcelas − total),
 * ou null quando não há parcelas ou a soma fecha ao centavo. É aviso, não
 * bloqueio: em PROD há pedidos cujas parcelas não somam o total de propósito.
 */
export function installmentsTotalMismatch(installments: InstallmentLike[], orderTotal: number): number | null {
  if (installments.length === 0) return null;
  const sum = installments.reduce((s, i) => s + (i.value || 0), 0);
  const diff = Math.round((sum - orderTotal) * 100) / 100;
  return Math.abs(diff) < 0.005 ? null : diff;
}

/**
 * Converte o `details` da API (chaves no formato `installments[2].due_date`,
 * `items[0].quantity` ou `company_id`) para as chaves do mapa de erros do
 * formulário (`installments.2.due_date`). Chaves sem índice passam como estão —
 * é o caso do backend antigo, que devolvia só `due_date`.
 */
export function detailsToFormErrors(details: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, msg] of Object.entries(details)) {
    out[key.replace(/\[(\d+)\]/g, '.$1')] = msg;
  }
  return out;
}

/** Remove do mapa os erros das parcelas (a pessoa mexeu numa linha; revalida ao salvar). */
export function withoutInstallmentErrors(errors: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, msg] of Object.entries(errors)) {
    if (!key.startsWith('installments.') && key !== 'due_date' && key !== 'value') out[key] = msg;
  }
  return out;
}
